// MOAT-01 — the measured pilot governing this shop (if any), read-only for every console role; owner/admin
// can disable it. Disabling is a frozen stop rule: every vendor immediately goes back to the control
// treatment (AI draft assistance hidden) and the stop is recorded with who did it. Only Kernvel staff
// can start a new pilot version. Backend: app/api/console_portal/pilot.py.
import { useCallback, useEffect, useState } from "react";

import { ApiError, apiGet, apiPost } from "../api";
import { useAuth } from "../auth";
import type { PilotReport } from "../pilot";
import PilotReportView from "./PilotReportView";

type State =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "none" }
  | { status: "loaded"; report: PilotReport };

export default function Pilot() {
  const { user } = useAuth();
  const [state, setState] = useState<State>({ status: "loading" });
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiGet<PilotReport | { pilot: null }>("/pilot");
      setState(data.pilot === null ? { status: "none" } : { status: "loaded", report: data as PilotReport });
    } catch (e) {
      setState({ status: "error", message: e instanceof ApiError ? e.message : "Could not load the pilot" });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canDisable = user?.role === "owner" || user?.role === "admin";

  const disable = async () => {
    setBusy(true);
    setErr(null);
    try {
      await apiPost("/pilot/disable", { reason: reason.trim() });
      setConfirming(false);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not disable the pilot");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Pilot</h1>
      </div>
      {state.status === "loading" && <p className="risk-sub">Loading…</p>}
      {state.status === "error" && <p className="risk-sub">Error: {state.message}</p>}
      {state.status === "none" && <p className="risk-sub">No measured pilot runs in this store.</p>}
      {state.status === "loaded" && (
        <>
          <PilotReportView report={state.report} />
          {canDisable && state.report.pilot.status === "active" && (
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card-cust">Turn the pilot off</div>
              <p className="risk-sub">
                Every vendor goes back to the no-assistance experience immediately. This cannot be undone here.
              </p>
              <label>
                Reason
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ display: "block", width: "100%", marginTop: 4 }}
                />
              </label>
              {!confirming ? (
                <button onClick={() => setConfirming(true)} disabled={reason.trim().length < 3} style={{ marginTop: 8 }}>
                  Disable pilot
                </button>
              ) : (
                <button onClick={() => void disable()} disabled={busy} style={{ marginTop: 8 }}>
                  {busy ? "Disabling…" : "Confirm: disable pilot"}
                </button>
              )}
              {err && <p className="risk-sub">Error: {err}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
